import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import produce from "immer";
import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SCHOOL_YEAR_ACTIVE_ID,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";
import {
  Permission,
  PermissionAction,
  StudentFilter as StudentFilterModel,
  StudentListDto,
  StudentListFilter,
  StudentListResource,
  TenantInfo,
} from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { studentCreatePageRoute } from "../StudentCreatePage/StudentCreatePage";
import { StudentTable } from "./StudentTable";
import { ListFooter } from "components";
import { StudentFilter } from "../StudentFilter";
import { FilterDrawerButton } from "components/common/FilterDrawerButton";
import { FilterDrawer } from "../FilterDrawer";

const routeName = "students";

export const studentListPageRoute: PageRoute = {
  handle: "Student Profiles",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

const ROOT_PERMISSION: Permission = Permission.Student;

export const StudentListPage = () => {
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentListFilter>({
    sortObjects: [{ property: pn<StudentListDto>("fullName"), isDesc: false }],
    studentFilter: {
      schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
    },
  });

  const [initListFilter] = useState<StudentListFilter>({
    sortObjects: [],
    studentFilter: {
      schoolYears: [SCHOOL_YEAR_ACTIVE_ID],
    },
    customFilters: [],
    pageSize: 20
  });
  const [listItems, setListItems] = useState<StudentListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>();

  // Filter values
  const [filterOpen, setFilterOpen] = useState(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter,
    setListFilter,
    fetchList: (listFilter) => {
      ApiService.students.getList(listFilter).then(setListItems);
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.students.getListTotal(listFilter).then(setTotal);
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.textingConversations
      .getTenantTextingInfo()
      .then(setTenantInfo);

    ApiService.students
      .getListResource()
      .then(setResource);
  };

  const getIdsMethods = () => ApiService.students.getIds(listFilter);

  const updateListFilter = (updatedFilter: StudentListFilter) => {
    setListFilter(updatedFilter);
    loadList(updatedFilter, { resetPage: true });
  };

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">
              {studentListPageRoute.handle}
            </Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(studentCreatePageRoute.getPath())
                  }
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <SearchBar
                size="small"
                onStoppedTyping={applySearch}
                setSearchFocused={setSearchFocused}
              />

              <FilterDrawerButton
                initListFilter={initListFilter}
                listFilter={listFilter}
                setFilterOpen={setFilterOpen}
                clearFilter={updateListFilter}
               />


            </Stack>
          }
          tableContainer={
            <StudentTable
              rootPermission={ROOT_PERMISSION}
              tenantTextingInfo={tenantInfo}
              tableContainerRef={tableContainerRef}
              students={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={getIdsMethods}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      {/* FILTER DRAWER */}
      <FilterDrawer
        listFilter={listFilter}
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onReset={() => {
          setListFilter(initListFilter);
          loadList(initListFilter, { resetPage: true });
        }}
        onApply={() => {
          setListFilter(
            produce(listFilter, (draft) => {
              draft.page = 1;
            })

          );

          loadList(
            produce(listFilter, (draft) => {
              draft.page = 1;
            }),
            { resetPage: true }
          );

          setFilterOpen(false);
        }}
      >
        <StudentFilter
          filter={listFilter || ({} as StudentListFilter)}
          setFilter={(filter) =>
            setListFilter(filter)
          }
        />
      </FilterDrawer>


      {/* LIST CONTEXT */}
      <ListContext.Provider
        value={{
          loadList: (options) =>
            loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
